'use client';

import { useEffect } from 'react';
import * as Sentry from '@sentry/nextjs';

/**
 * Global error boundary — last resort when the root layout itself crashes.
 * Inline styles only (CSS/Tailwind may not be loaded).
 * Supports system light/dark theme via prefers-color-scheme.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>Something went wrong | Virza</title>
        <link rel="icon" href="/favicon.svg" type="image/svg+xml" />
        <style
          dangerouslySetInnerHTML={{
            __html: `
          *{margin:0;padding:0;box-sizing:border-box}
          @keyframes fadeIn{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
          :root{--bg:#ffffff;--fg:#09090b;--muted:#71717a;--card:#f4f4f5;--border:#e4e4e7;--error-bg:#fef2f2;--error-fg:#dc2626;--btn-bg:#09090b;--btn-fg:#fafafa;--btn2-bg:#f4f4f5;--btn2-border:#e4e4e7;--btn2-fg:#52525b;--mark:#09090b;--mark-o:0.7;--brand:#a1a1aa}
          @media(prefers-color-scheme:dark){:root{--bg:#09090b;--fg:#fafafa;--muted:rgba(255,255,255,0.4);--card:rgba(255,255,255,0.04);--border:rgba(255,255,255,0.08);--error-bg:rgba(220,38,38,0.08);--error-fg:#f87171;--btn-bg:#fafafa;--btn-fg:#09090b;--btn2-bg:rgba(255,255,255,0.05);--btn2-border:rgba(255,255,255,0.08);--btn2-fg:rgba(255,255,255,0.6);--mark:#fafafa;--brand:rgba(255,255,255,0.25)}}
          body{min-height:100dvh;display:flex;align-items:center;justify-content:center;font-family:ui-sans-serif,system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;background:var(--bg);color:var(--fg);padding:24px;-webkit-font-smoothing:antialiased}
          .c{text-align:center;max-width:480px;width:100%;animation:fadeIn .6s cubic-bezier(.16,1,.3,1)}
          .logo{display:inline-flex;align-items:center;gap:12px;text-decoration:none;color:var(--fg);margin-bottom:56px}
          .logo-t{font-size:22px;font-weight:600;letter-spacing:-0.025em}
          .icon-box{width:64px;height:64px;border-radius:18px;background:var(--error-bg);display:flex;align-items:center;justify-content:center;margin:0 auto 24px}
          h1{font-size:clamp(24px,4vw,32px);font-weight:600;letter-spacing:-0.025em;margin-bottom:12px}
          .desc{font-size:15px;color:var(--muted);line-height:1.65;margin-bottom:36px}
          .acts{display:flex;gap:12px;justify-content:center;flex-wrap:wrap}
          .btn{height:48px;padding:0 28px;border-radius:12px;font-size:15px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:10px;transition:all 150ms;text-decoration:none;border:none}
          .btn:active{transform:scale(.97)}
          .bp{background:var(--btn-bg);color:var(--btn-fg)}
          .bp:hover{opacity:.9}
          .bs{background:var(--btn2-bg);border:1px solid var(--btn2-border);color:var(--btn2-fg)}
          .bs:hover{opacity:.8}
          .eid{font-size:12px;color:var(--muted);opacity:.5;margin-top:24px;font-family:ui-monospace,monospace}
          .ft{display:flex;align-items:center;justify-content:center;gap:10px;margin-top:48px}
          .ft-t{font-size:13px;color:var(--brand);letter-spacing:-0.01em}
        `,
          }}
        />
      </head>
      <body>
        <div className="c">
          {/* Logo — intentional <a> tag: global error boundary renders outside
              the Next.js router, so <Link> is not available */}
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
            <a href="/" className="logo">
              <svg width="36" height="36" viewBox="0 0 512 512" fill="none">
                <path d="M84 64 L156 64 L256 352 L256 448 L69 89 Z" fill="var(--mark)" />
                <path
                  d="M356 64 L428 64 L443 89 L256 448 L256 352 Z"
                  fill="var(--mark)"
                  opacity="var(--mark-o)"
                />
              </svg>
              <span className="logo-t">Virza</span>
            </a>
          </div>

          {/* Icon */}
          <div className="icon-box">
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--error-fg)"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>

          <h1>Something went wrong</h1>
          <p className="desc">
            A critical error occurred while loading the application. Our team has been automatically
            notified.
          </p>

          <div className="acts">
            <button onClick={reset} className="btn bp">
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="23 4 23 10 17 10" />
                <path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" />
              </svg>
              Try again
            </button>
            {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
            <a href="/" className="btn bs">
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                <polyline points="9 22 9 12 15 12 15 22" />
              </svg>
              Go Home
            </a>
          </div>

          {error.digest && <p className="eid">Error ID: {error.digest}</p>}

          <div className="ft">
            <svg width="16" height="16" viewBox="0 0 512 512" fill="none">
              <path d="M84 64 L156 64 L256 352 L256 448 L69 89 Z" fill="var(--brand)" />
              <path
                d="M356 64 L428 64 L443 89 L256 448 L256 352 Z"
                fill="var(--brand)"
                opacity="0.7"
              />
            </svg>
            <span className="ft-t">Research without the chaos</span>
          </div>
        </div>
      </body>
    </html>
  );
}
